import React from "react";
import { useRecoilValue, useRecoilState } from "recoil";
import useConfirmDialog from "../../../hooks/useConfirmDialog";
import {
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  InputAdornment,
  IconButton,
  Stack,
  Chip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { Routes, Route, Navigate } from "react-router-dom";

import { useNavigate } from "react-router-dom";
import ClientData, {
  deleteClient,
  ClientSubscriptionFilters,
} from "../../../data/ClientData";
import UserData from "../../../data/UserData";
import { coachUserUpdateFromBasicInfo } from "./clients/utils/coachClientUserUpdates";
import ClientTable from "./clients/ClientTable";
import ClientSummary from "./clients/ClientSummary";
import ClientProgressPage from "./clients/ClientProgressPage";
import ClientEditDialog from "./clients/ClientEditDialog";
import userDataState from "../../../atoms/userDataAtom";
import useEvents from "../../../hooks/useEvents";
import organizationIdState from "../../../atoms/organizationIdAtom";
import { WebAnalytics, ANALYTICS_EVENTS } from "@mighty45/analytics-shared";
import {
  clientTableFilterState,
  aggregatedClientsState,
} from "../../../hooks/useClientsAggregate";
import PageHeader from "../../../components/PageHeader";
import PageContent from "../../../components/PageContent";
import StandardButton from "../../../components/StandardButton";
import useEntitlements from "../../../hooks/useEntitlements";
import NewColors from "../../../themes/NewColors";

const FilterSelection = ({
  filter,
  onFilterChanged,
  analytics,
  organizationId,
}) => {
  return (
    <RadioGroup
      row
      aria-labelledby="demo-controlled-radio-buttons-group"
      name="controlled-radio-buttons-group"
      value={filter}
      sx={{
        flexWrap: "wrap",
        gap: 0.25,
        "& .MuiFormControlLabel-root": { mr: 1 },
        "& .MuiFormControlLabel-label": { fontSize: "0.85rem" },
      }}
      onChange={(event) => {
        const newFilter = event.target.value;

        // Track filter change
        analytics.trackCustomEvent("user_action", {
          action: "client_filter_changed",
          element: "filter_radio",
          from_filter: filter,
          to_filter: newFilter,
          organization_id: organizationId,
        });

        onFilterChanged(newFilter);
      }}
    >
      <FormControlLabel
        value={ClientSubscriptionFilters.all}
        control={<Radio />}
        label="All"
      />
      <FormControlLabel
        value={ClientSubscriptionFilters.active}
        control={<Radio />}
        label="Active"
      />
      <FormControlLabel
        value={ClientSubscriptionFilters.disabled}
        control={<Radio />}
        label="Disabled"
      />
    </RadioGroup>
  );
};

const ClientsPage = ({ additionalInfo }) => {
  const navigate = useNavigate();
  const userDataValues = useRecoilValue(userDataState);
  const organizationId = useRecoilValue(organizationIdState);

  const [filter, setFilter] = useRecoilState(clientTableFilterState);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [editClientOpen, setClientEditOpen] = React.useState(false);
  const [editingClient, setEditingClient] = React.useState(null);
  const events = useEvents();
  const { showConfirmDialog, ConfirmDialog } = useConfirmDialog();
  const selectedClientRef = React.useRef(null);

  // Get clients from state
  const allClients = useRecoilValue(aggregatedClientsState);
  const {
    tier,
    activeClientLimit,
    isUnlimitedClients,
    canAddActiveClient,
  } = useEntitlements();

  const lastActiveCountRef = React.useRef(0);

  const activeClientCount = React.useMemo(() => {
    if (filter === ClientSubscriptionFilters.active) {
      return allClients.length;
    }
    if (filter === ClientSubscriptionFilters.disabled) {
      return lastActiveCountRef.current;
    }
    return allClients.filter((client) => {
      const status = client.status || "active";
      return status !== "disabled" && status !== "inactive";
    }).length;
  }, [allClients, filter]);

  React.useEffect(() => {
    if (filter !== ClientSubscriptionFilters.disabled) {
      lastActiveCountRef.current = activeClientCount;
    }
  }, [activeClientCount, filter]);

  const canAdd = canAddActiveClient(activeClientCount);

  // Filter clients based on search query
  const filteredClients = React.useMemo(() => {
    if (!searchQuery.trim()) {
      return allClients;
    }
    const query = searchQuery.toLowerCase().trim();
    return allClients.filter((client) => {
      const firstName = (client.firstName || "").toLowerCase();
      const lastName = (client.lastName || "").toLowerCase();
      const fullName = `${firstName} ${lastName}`.trim();
      return (
        firstName.includes(query) ||
        lastName.includes(query) ||
        fullName.includes(query)
      );
    });
  }, [allClients, searchQuery]);

  // For event-driven analytics (clicks, actions, etc.)
  const analytics = React.useMemo(() => new WebAnalytics({}), []);

  // Track page view and filter changes
  React.useEffect(() => {
    // Only track if we have required data
    if (!organizationId || !userDataValues?.role) {
      return;
    }

    // Track screen view on mount
    analytics.trackScreenView("Clients", {
      organization_id: organizationId,
      user_role: userDataValues?.role,
      current_filter: filter,
    });

    // Track feature usage
    analytics.trackCustomEvent(ANALYTICS_EVENTS.FEATURE_USED, {
      feature: "client_management",
      organization_id: organizationId,
    });
  }, [organizationId, userDataValues?.role]); // Add dependencies

  // Track filter changes separately
  React.useEffect(() => {
    // Only track if we have required data
    if (filter && organizationId) {
      analytics.trackCustomEvent(ANALYTICS_EVENTS.FILTER_APPLIED, {
        screen: "Clients",
        filter: filter,
        organization_id: organizationId,
      });
    }
  }, [filter, organizationId, analytics]);

  const onBackClickCallback = React.useCallback(() => {
    navigate("/home/clients");
  }, [navigate]);

  const confirmAlertCallback = React.useCallback(() => {
    const limitLabel = isUnlimitedClients
      ? "unlimited"
      : String(activeClientLimit);
    showConfirmDialog({
      title: `Maximum number of clients reached`,
      message: `You have reached the maximum number of active clients on your ${tier} plan (${activeClientCount}/${limitLabel}). Upgrade your plan to add more clients.`,
      confirmText: "Upgrade Plan",
      cancelText: "OK",
      onConfirm: () => {
        analytics.trackCustomEvent(ANALYTICS_EVENTS.SUBSCRIPTION_ACTION, {
          action: "upgrade_plan_clicked",
          source: "client_limit_modal",
          organization_id: organizationId,
        });
        navigate("/subscribe");
      },
      onCancel: () => {
        analytics.trackCustomEvent("user_action", {
          action: "upgrade_plan_dismissed",
          element: "cancel_button",
          source: "client_limit_modal",
          organization_id: organizationId,
        });
      },
    });
  }, [
    navigate,
    showConfirmDialog,
    analytics,
    organizationId,
    tier,
    activeClientCount,
    activeClientLimit,
    isUnlimitedClients,
  ]);

  const addClientCallback = React.useCallback(
    (isOk) => {
      if (isOk) {
        analytics.trackCustomEvent(ANALYTICS_EVENTS.FEATURE_USED, {
          feature: "client_creation",
          organization_id: organizationId,
        });
        setClientEditOpen(true);
        return;
      }

      confirmAlertCallback();
    },
    [confirmAlertCallback, analytics, organizationId],
  );

  return (
    <>
      <Routes>
        <Route
          path=""
          element={
            <PageContent
              header={
                <PageHeader 
                  title="Clients" 
                  subtitle="Manage your organization's clients and track their progress"
                />
              }
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flex: 1,
                  minHeight: 0,
                }}
              >
                <Box
                  sx={{
                    mb: 1.25,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1.5,
                    flexWrap: "wrap",
                  }}
                >
                  <TextField
                    placeholder="Search clients by name..."
                    variant="outlined"
                    size="small"
                    value={searchQuery}
                    onChange={(event) => {
                      setSearchQuery(event.target.value);
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                      endAdornment: searchQuery && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSearchQuery("");
                            }}
                            aria-label="Clear search"
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      minWidth: { xs: "100%", sm: 280 },
                      maxWidth: 420,
                      flex: { xs: "1 1 100%", sm: "0 1 420px" },
                    }}
                  />
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ flexWrap: "wrap", justifyContent: "flex-end", rowGap: 1 }}
                  >
                    <Chip
                      size="small"
                      label={
                        isUnlimitedClients
                          ? `${activeClientCount} active`
                          : `${activeClientCount} / ${activeClientLimit} active`
                      }
                      onClick={
                        !canAdd
                          ? () => {
                              navigate("/subscribe");
                            }
                          : undefined
                      }
                      sx={{
                        fontWeight: 600,
                        backgroundColor: canAdd
                          ? "rgba(0, 0, 0, 0.06)"
                          : NewColors.brand?.primary || "#C40F0D",
                        color: canAdd ? "text.primary" : "#fff",
                        cursor: canAdd ? "default" : "pointer",
                      }}
                    />
                    {!canAdd && (
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", maxWidth: 160 }}
                      >
                        Upgrade to add more
                      </Typography>
                    )}
                    {userDataValues?.role === "admin" && (
                      <FilterSelection
                        filter={filter}
                        onFilterChanged={setFilter}
                        analytics={analytics}
                        organizationId={organizationId}
                      />
                    )}
                    <StandardButton
                      variant="primary"
                      onClick={() => {
                        addClientCallback(canAdd);
                        const logEvent = {
                          buttonName: "Added Client",
                          pageName: "Clients",
                        };
                        events.logButtonClick(logEvent);

                        analytics.trackCustomEvent("user_action", {
                          action: "add_client_clicked",
                          element: "add_client_button",
                          can_add_client: canAdd,
                          organization_id: organizationId,
                        });
                      }}
                    >
                      Add New Client
                    </StandardButton>
                  </Stack>
                </Box>
                <ClientTable
                  clients={filteredClients}
                  additionalInfo={additionalInfo}
                  filter={filter}
                  onClientClicked={(client) => {
                    selectedClientRef.current = client.uid;
                    localStorage.setItem("selectedClient", client.uid);
                    navigate(
                      `/home/clients/summary/${client.uid}/${organizationId}`,
                    );
                    const logEvent = {
                      subpage: "Client Summary",
                      pageName: "Clients",
                      uid: client.uid,
                    };
                    events.logSubpageAccess(logEvent);

                    // Track with our analytics
                    analytics.trackCustomEvent("user_action", {
                      action: "client_clicked",
                      element: "client_row",
                      client_id: client.uid,
                      client_status: client.status,
                      organization_id: organizationId,
                      destination: "client_summary",
                    });
                  }}
                  deleteClientClicked={(client) => {
                    showConfirmDialog({
                      title: `Delete the client ${client.firstName} ${client.lastName}?`,
                      message:
                        "This process cannot be undone and is permanent. All client data and history will be lost! Might be better to keep them disabled. Continue with delete?",
                      confirmText: "Yes",
                      cancelText: "No",
                      onConfirm: async () => {
                        deleteClient({
                          organizationId: organizationId,
                          uid: client.uid,
                        });
                        const logEvent = {
                          buttonName: "Deleted Client",
                          pageName: "Clients",
                        };
                        events.logButtonClick(logEvent);
                      },
                      onCancel: () => {
                        const logEvent = {
                          buttonName: "Canceled Client Delete",
                          pageName: "Clients",
                        };
                        events.logButtonClick(logEvent);
                      },
                    });
                  }}
                  editClientClicked={(client) => {
                    setEditingClient(client);
                    setClientEditOpen(true);
                    const logEvent = {
                      buttonName: "Edit Client",
                      pageName: "Clients",
                    };
                    events.logButtonClick(logEvent);
                  }}
                />
                {editClientOpen && (
                  <ClientEditDialog
                    client={editingClient}
                    open={editClientOpen}
                    onDialogClosed={async (data) => {
                      if (!data) {
                        setClientEditOpen(false);
                        setEditingClient(null);
                        return;
                      }

                      try {
                        if (editingClient) {
                          await UserData.updateUser(
                            editingClient.uid,
                            coachUserUpdateFromBasicInfo(editingClient, data),
                          );

                          await ClientData.updateClient(editingClient.uid, {
                            statistics: {
                              experience: data.experience,
                              height: data.height,
                              sex: data.sex,
                              goal: data.goal,
                              weight: data.weight,
                              startingWeight: data.startingWeight,
                              birthDate: data.birthDate,
                              activityLevel: data.activityLevel,
                              untrackedMeals: data.untrackedMeals,
                            },
                            language: data.language,
                            status: data.status,
                            tier: data.tier,
                            statisticsChanged: new Date(),
                            renewDate: data.renewDate,
                            checkInDay: data.checkInDay,
                            protocolAge: data.protocolAge,
                          });

                          const logEvent = {
                            buttonName: "Updated Client",
                            pageName: "Clients",
                          };
                          events.logButtonClick(logEvent);
                        } else {
                          const uid = await ClientData.addClient({
                            firstName: data.firstName,
                            lastName: data.lastName,
                            email: data.email,
                            language: data.language,
                            tier: data.tier,
                            renewDate: data.renewDate,
                            checkInDay: data.checkInDay,
                            status: data.status,
                            isDeleted: false,
                            experience: data.experience,
                            height: data.height,
                            sex: data.sex,
                            goal: data.goal,
                            weight: data.weight,
                            startingWeight: data.startingWeight,
                            birthDate: data.birthDate,
                            activityLevel: data.activityLevel,
                            coachId: localStorage.getItem("uid"),
                          });
                          if (uid) {
                            selectedClientRef.current = uid;
                            navigate(
                              `/home/clients/summary/${uid}/${organizationId}`,
                            );
                          }
                        }
                        setClientEditOpen(false);
                        setEditingClient(null);
                      } catch (err) {
                        if (err?.code === "CLIENT_LIMIT" || err?.status === 402) {
                          setClientEditOpen(false);
                          setEditingClient(null);
                          confirmAlertCallback();
                          return;
                        }
                        throw err;
                      }
                    }}
                  />
                )}
              </Box>
            </PageContent>
          }
        />
        <Route
          path="summary/:uid/:organizationId"
          element={(() => {
            const selectedClient =
              selectedClientRef.current ||
              localStorage.getItem("selectedClient");
            if (!selectedClient) {
              return <Navigate to="/home/clients" replace />;
            }
            return (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flex: 1,
                  px: { xs: 2, sm: 3 },
                }}
              >
                <ClientSummary onBackClick={onBackClickCallback} />
              </Box>
            );
          })()}
        />
        <Route
          path="progress/:uid/:organizationId"
          element={
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                px: { xs: 2, sm: 3 },
              }}
            >
              <ClientProgressPage />
            </Box>
          }
        />
      </Routes>
      <ConfirmDialog />
    </>
  );
};

export default ClientsPage;
